import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ArrowUpRight } from 'lucide-react';
import { fetchAds, selectFirstAdSlot } from '../../store/slices/adsSlice';

const PAGE_GUTTERS = 'w-full mx-auto px-4 sm:px-6 md:px-8 lg:px-12 xl:px-28';

/**
 * Renders one ad slot from GET /api/ads?active_only=true: its image, its text content
 * (headline, description, call to action), or both. Renders nothing while loading, or
 * when the slot is missing, disabled or has nothing to show.
 *
 * slotId    one slot id, or several in order of preference (first with something to show wins)
 * variant   'banner'  – text runs under the image (wide creatives)
 *           'feature' – image beside the text on wider screens (poster creatives)
 *           'compact' – everything stacked, for narrow columns such as sidebars
 * container { className, maxWidthClass } wraps the ad in a full-width page band
 */
export const AdSlot = ({ slotId, variant = 'banner', className = '', container }) => {
  const dispatch = useDispatch();
  const slotIds = Array.isArray(slotId) ? slotId : [slotId];
  const slot = useSelector((state) => selectFirstAdSlot(state, slotIds));
  // Remember which image failed so a new creative URL gets a fresh attempt
  const [failedImage, setFailedImage] = useState(null);

  useEffect(() => {
    dispatch(fetchAds(true));
  }, [dispatch]);

  if (!slot || !slot.enabled) return null;

  const showImage = Boolean(slot.imageUrl) && failedImage !== slot.imageUrl;
  if (!showImage && !slot.hasContent) return null;

  const textOnly = !showImage;
  const sideBySide = showImage && slot.hasContent && variant === 'feature';
  const stacked = variant === 'compact';

  const image = showImage && (
    <img
      src={slot.imageUrl}
      alt={slot.alt}
      width={slot.width || undefined}
      height={slot.height || undefined}
      loading="lazy"
      decoding="async"
      onError={() => setFailedImage(slot.imageUrl)}
      className={`block w-full h-auto ${sideBySide ? 'md:h-full md:object-cover' : ''}`}
    />
  );

  const text = slot.hasContent && (
    <div className="min-w-0">
      {slot.headline && (
        <p className={`font-extrabold text-slate-900 leading-snug ${textOnly ? 'text-base sm:text-lg' : 'text-sm sm:text-base'}`}>
          {slot.headline}
        </p>
      )}
      {slot.description && (
        <p className="mt-1 text-xs sm:text-sm text-slate-500 leading-relaxed line-clamp-3">{slot.description}</p>
      )}
    </div>
  );

  // The whole card is the link, so the button is presentational
  const cta = slot.hasContent && slot.href && (
    <span
      className={`inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-xl bg-[#008080] group-hover:bg-[#006666] text-white text-xs font-bold whitespace-nowrap transition-colors flex-shrink-0 ${
        sideBySide || stacked ? 'self-start' : 'self-start sm:self-auto'
      }`}
    >
      {slot.ctaLabel || 'Learn more'}
      <ArrowUpRight className="w-3.5 h-3.5" />
    </span>
  );

  let body;
  if (!slot.hasContent) {
    body = image;
  } else if (sideBySide) {
    body = (
      <div className="md:grid md:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        <div className="bg-slate-50">{image}</div>
        <div className="flex flex-col justify-center gap-4 p-5 sm:p-6">
          {text}
          {cta}
        </div>
      </div>
    );
  } else if (stacked) {
    body = (
      <>
        {image}
        <div className={`flex flex-col gap-3 ${textOnly ? 'p-4 sm:p-5' : 'px-4 py-3.5 border-t border-slate-100'}`}>
          {text}
          {cta}
        </div>
      </>
    );
  } else {
    body = (
      <>
        {image}
        <div
          className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-6 ${
            textOnly ? 'p-5 sm:p-6' : 'px-4 sm:px-5 py-3.5 border-t border-slate-100'
          }`}
        >
          {text}
          {cta}
        </div>
      </>
    );
  }

  const linkProps = slot.href
    ? {
        href: slot.href,
        ...(slot.isExternal && { target: '_blank', rel: 'noopener noreferrer sponsored' }),
      }
    : null;
  const Frame = linkProps ? 'a' : 'div';
  const frameClass = [
    'group block overflow-hidden rounded-2xl border border-slate-200/90 shadow-sm',
    textOnly ? 'bg-gradient-to-br from-teal-50 via-white to-white' : 'bg-white',
    linkProps ? 'hover:border-slate-300 hover:shadow-md transition-all duration-200' : '',
  ].join(' ');

  // Cap at the creative's own width so small banners aren't upscaled and blurred.
  // Compact ads always match their column so sidebar cards line up.
  const capWidth = showImage && !sideBySide && !stacked ? slot.width : null;
  const attribution = slot.sponsor ? `Sponsored by ${slot.sponsor}` : slot.domain;

  const card = (
    <aside
      id={container ? undefined : `ad-${slot.id}`}
      aria-label="Advertisement"
      className={`mx-auto ${textOnly && !stacked ? 'max-w-4xl' : ''} ${container ? '' : className}`}
      style={capWidth ? { maxWidth: capWidth } : undefined}
    >
      <div className="flex items-center justify-between gap-3 mb-2">
        <span className="text-[11px] font-bold tracking-widest text-slate-400 uppercase">Advertisement</span>
        {attribution && <span className="text-[11px] font-medium text-slate-400 truncate">{attribution}</span>}
      </div>
      <Frame {...linkProps} className={frameClass}>
        {body}
      </Frame>
    </aside>
  );

  if (!container) return card;

  return (
    <div id={`ad-${slot.id}`} className={container.className}>
      <div className={`${PAGE_GUTTERS} ${container.maxWidthClass || ''}`}>{card}</div>
    </div>
  );
};

export default AdSlot;
